import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useSnackbar } from 'notistack';

import api from '../../../../services/api';
import { Main } from '../../../../components/Main';

// Configurações › Etiquetas (ET3 · aprovado): modelo é ZPL cru com
// {{campos}} — sem editor visual, por decisão (quem não sabe ZPL duplica
// o modelo padrão e mexe nos números). Prévia em PNG no servidor, com
// dados de exemplo. Quem imprime é o coletor 4LogX Operation (TCP 9100).

interface ITemplate {
  id: number;
  nome: string;
  zpl: string;
  larguraMm: number;
  alturaMm: number;
  dpi: number;
  padrao: boolean;
}

interface IPrinter {
  id: number;
  nome: string;
  host: string;
  porta: number;
  dpi: number;
  padrao: boolean;
}

interface IJob {
  id: number;
  descricao: string;
  impressora: string;
  copias: number;
  status: string;
  tentativas: number;
  erro: string | null;
  createdAt: string;
}

const JOB_CHIP: Record<string, { label: string; bg: string; fg: string }> = {
  pending: { label: 'NA FILA', bg: '#E8EFF8', fg: '#0E52A0' },
  claimed: { label: 'IMPRIMINDO', bg: '#FBF0DC', fg: '#9A6A0B' },
  printed: { label: 'IMPRESSA', bg: '#EAF5E8', fg: '#2E7D32' },
  failed: { label: 'FALHOU', bg: '#FAE4E4', fg: '#C43D3D' },
  canceled: { label: 'CANCELADA', bg: '#EDF1F5', fg: '#5A6B7A' },
};

export const Labels = () => {
  const [tab, setTab] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  // ---- modelos + editor ----
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [campos, setCampos] = useState<string[]>([]);
  const [previaDisponivel, setPreviaDisponivel] = useState(true);
  const [editing, setEditing] = useState<ITemplate | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const zplRef = useRef<HTMLTextAreaElement | null>(null);
  // dados da prévia editáveis: cola o dado real (até o HEX da TAG — o
  // servidor traduz) e a prévia vira exatamente o que sai impresso
  const [amostra, setAmostra] = useState<Record<string, string>>({});
  const amostraRef = useRef(amostra);
  amostraRef.current = amostra;
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewSeq = useRef(0);

  const loadTemplates = useCallback(async () => {
    try {
      const [modelos, meta] = await Promise.all([
        api.get('/labels/modelos'),
        api.get('/labels/campos'),
      ]);
      const ts: ITemplate[] = modelos.data?.modelos || [];
      setTemplates(ts);
      setCampos(meta.data?.campos || []);
      setPreviaDisponivel(meta.data?.previaDisponivel !== false);
      setEditing((current) => current ?? ts[0] ?? null);
    } catch {
      // API sem o módulo ainda: tela fica vazia sem quebrar
    }
  }, []);

  // prévia com debounce (600ms) e guarda de resposta fora de ordem
  const requestPreview = useCallback(
    (template: ITemplate) => {
      if (!previaDisponivel) return;
      if (previewTimer.current) clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(async () => {
        const seq = ++previewSeq.current;
        try {
          const response = await api.post(
            '/labels/previa',
            { ...template, amostra: amostraRef.current },
            { responseType: 'blob' },
          );
          if (seq !== previewSeq.current) return;
          setPreviewError(null);
          setPreviewUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return URL.createObjectURL(response.data);
          });
        } catch (error: any) {
          if (seq !== previewSeq.current) return;
          setPreviewError('Modelo inválido ou prévia indisponível.');
        }
      }, 600);
    },
    [previaDisponivel],
  );

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (editing) requestPreview(editing);
  }, [editing, amostra, requestPreview]);

  const updateEditing = (patch: Partial<ITemplate>) =>
    setEditing((current) => (current ? { ...current, ...patch } : current));

  const insertField = (campo: string) => {
    const area = zplRef.current;
    if (!editing) return;
    const token = `{{${campo}}}`;
    if (!area) {
      updateEditing({ zpl: editing.zpl + token });
      return;
    }
    const start = area.selectionStart ?? editing.zpl.length;
    const end = area.selectionEnd ?? start;
    const zpl = editing.zpl.slice(0, start) + token + editing.zpl.slice(end);
    updateEditing({ zpl });
    requestAnimationFrame(() => {
      area.focus();
      area.selectionStart = area.selectionEnd = start + token.length;
    });
  };

  const saveTemplate = async () => {
    if (!editing) return;
    try {
      const response =
        editing.id > 0
          ? await api.put(`/labels/modelos/${editing.id}`, editing)
          : await api.post('/labels/modelos', editing);
      if (response.data?.error) {
        enqueueSnackbar(response.data.error, { variant: 'error' });
        return;
      }
      enqueueSnackbar('Modelo salvo.', { variant: 'success' });
      setEditing(response.data?.modelo || null);
      loadTemplates();
    } catch {
      enqueueSnackbar('Falha ao salvar o modelo.', { variant: 'error' });
    }
  };

  const duplicateTemplate = () => {
    if (!editing) return;
    setEditing({ ...editing, id: 0, nome: `${editing.nome} (cópia)`, padrao: false });
  };

  const deleteTemplate = async (t: ITemplate) => {
    if (!window.confirm(`Excluir o modelo "${t.nome}"?`)) return;
    await api.delete(`/labels/modelos/${t.id}`);
    setEditing(null);
    loadTemplates();
  };

  // ---- impressoras ----
  const [printers, setPrinters] = useState<IPrinter[]>([]);
  const [printerForm, setPrinterForm] = useState<IPrinter | null>(null);

  const loadPrinters = useCallback(async () => {
    try {
      const response = await api.get('/labels/impressoras');
      setPrinters(response.data?.impressoras || []);
    } catch {
      setPrinters([]);
    }
  }, []);

  useEffect(() => {
    loadPrinters();
  }, [loadPrinters]);

  const savePrinter = async () => {
    if (!printerForm) return;
    const response =
      printerForm.id > 0
        ? await api.put(`/labels/impressoras/${printerForm.id}`, printerForm)
        : await api.post('/labels/impressoras', printerForm);
    if (response.data?.error) {
      enqueueSnackbar(response.data.error, { variant: 'error' });
      return;
    }
    enqueueSnackbar('Impressora salva.', { variant: 'success' });
    setPrinterForm(null);
    loadPrinters();
  };

  const testPrinter = async (p: IPrinter) => {
    await api.post(`/labels/impressoras/${p.id}/teste`);
    enqueueSnackbar(
      'Etiqueta de teste na fila — o coletor imprime em instantes (acompanhe na aba Fila).',
      { variant: 'success' },
    );
  };

  const deletePrinter = async (p: IPrinter) => {
    if (!window.confirm(`Excluir a impressora "${p.nome}"?`)) return;
    await api.delete(`/labels/impressoras/${p.id}`);
    loadPrinters();
  };

  // ---- fila ----
  const [jobs, setJobs] = useState<IJob[] | null>(null);
  const queueTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadQueue = useCallback(async () => {
    try {
      const response = await api.get('/labels/fila');
      setJobs(response.data?.jobs || []);
    } catch {
      setJobs([]);
    }
  }, []);

  useEffect(() => {
    if (tab !== 2) return;
    loadQueue();
    queueTimer.current = setInterval(loadQueue, 10000);
    return () => {
      if (queueTimer.current) clearInterval(queueTimer.current);
    };
  }, [tab, loadQueue]);

  return (
    <Main title="Etiquetas">
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        indicatorColor="primary"
        textColor="primary"
        style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', marginBottom: 16 }}
      >
        <Tab label="Modelos" />
        <Tab label="Impressoras" />
        <Tab label="Fila" />
      </Tabs>

      {/* ===== MODELOS ===== */}
      {tab === 0 && (
        <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
          <Paper style={{ flex: '0 1 240px', alignSelf: 'flex-start' }}>
            <List dense>
              {templates.map((t) => (
                <ListItem
                  key={t.id}
                  button
                  selected={editing?.id === t.id}
                  onClick={() => setEditing(t)}
                >
                  <ListItemText
                    primary={`${t.nome}${t.padrao ? ' ★' : ''}`}
                    secondary={`${t.larguraMm}×${t.alturaMm} mm · ${t.dpi} dpi`}
                  />
                  <IconButton size="small" onClick={() => deleteTemplate(t)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
            <Box p={1}>
              <Button
                fullWidth
                size="small"
                color="primary"
                startIcon={<AddIcon />}
                onClick={duplicateTemplate}
                disabled={!editing}
              >
                Duplicar selecionado
              </Button>
            </Box>
          </Paper>

          {editing && (
            <Paper style={{ flex: '2 1 420px', padding: 16 }}>
              <Box display="flex" style={{ gap: 10 }} mb={1}>
                <TextField
                  size="small"
                  variant="outlined"
                  label="Nome"
                  value={editing.nome}
                  onChange={(e) => updateEditing({ nome: e.target.value })}
                  style={{ flex: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={editing.padrao}
                      onChange={(e) => updateEditing({ padrao: e.target.checked })}
                    />
                  }
                  label="Padrão"
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Clique num campo para inserir no ponto do cursor:
              </Typography>
              <Box display="flex" style={{ gap: 6 }} flexWrap="wrap" my={1}>
                {campos.map((campo) => (
                  <Chip
                    key={campo}
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`{{${campo}}}`}
                    onClick={() => insertField(campo)}
                  />
                ))}
              </Box>
              <textarea
                ref={zplRef}
                value={editing.zpl}
                onChange={(e) => updateEditing({ zpl: e.target.value })}
                spellCheck={false}
                style={{
                  width: '100%',
                  minHeight: 220,
                  fontFamily: 'monospace',
                  fontSize: 12.5,
                  background: '#16232E',
                  color: '#C8E6C9',
                  border: 'none',
                  borderRadius: 8,
                  padding: 14,
                  resize: 'vertical',
                }}
              />
              <Box display="flex" style={{ gap: 10 }} mt={1} flexWrap="wrap">
                <TextField
                  size="small"
                  variant="outlined"
                  type="number"
                  label="Largura (mm)"
                  value={editing.larguraMm}
                  onChange={(e) =>
                    updateEditing({ larguraMm: Number(e.target.value) })
                  }
                  style={{ width: 120 }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  type="number"
                  label="Altura (mm)"
                  value={editing.alturaMm}
                  onChange={(e) =>
                    updateEditing({ alturaMm: Number(e.target.value) })
                  }
                  style={{ width: 120 }}
                />
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="DPI"
                  value={editing.dpi}
                  onChange={(e) => updateEditing({ dpi: Number(e.target.value) })}
                  style={{ width: 100 }}
                >
                  {[203, 300, 600].map((dpi) => (
                    <MenuItem key={dpi} value={dpi}>
                      {dpi}
                    </MenuItem>
                  ))}
                </TextField>
                <Box flex={1} />
                <Button variant="contained" color="primary" onClick={saveTemplate}>
                  Salvar
                </Button>
              </Box>
            </Paper>
          )}

          <Paper
            style={{
              flex: '1 1 280px',
              padding: 16,
              textAlign: 'center',
              alignSelf: 'flex-start',
            }}
          >
            <Typography variant="overline" color="textSecondary">
              Prévia (dados de exemplo)
            </Typography>
            <Box mt={1}>
              {!previaDisponivel ? (
                <Typography variant="body2" color="textSecondary">
                  Prévia desligada no servidor.
                </Typography>
              ) : previewError ? (
                <Typography variant="body2" style={{ color: '#C43D3D' }}>
                  {previewError}
                </Typography>
              ) : previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Prévia da etiqueta"
                  style={{
                    maxWidth: '100%',
                    border: '1px solid #999',
                    boxShadow: '0 2px 8px rgba(0,0,0,.15)',
                  }}
                />
              ) : (
                <CircularProgress size={22} />
              )}
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: 'block', marginTop: 8 }}
            >
              O QR carrega a TAG — o mesmo código que o Operation bipa na
              separação. Atualiza sozinha enquanto edita.
            </Typography>
            <Box mt={2} textAlign="left">
              <Typography variant="overline" color="textSecondary">
                Dados da prévia — cole o dado real para testar
              </Typography>
              {campos.map((campo) => (
                <TextField
                  key={campo}
                  fullWidth
                  margin="dense"
                  size="small"
                  variant="outlined"
                  label={campo}
                  placeholder="(exemplo padrão)"
                  value={amostra[campo] || ''}
                  onChange={(e) =>
                    setAmostra((current) => ({
                      ...current,
                      [campo]: e.target.value,
                    }))
                  }
                />
              ))}
              <Typography variant="caption" color="textSecondary">
                Pode colar a TAG em HEX (54502E…) — o servidor traduz para o
                legível, igual sai na etiqueta.
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ===== IMPRESSORAS ===== */}
      {tab === 1 && (
        <Paper style={{ padding: 16 }}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setPrinterForm({
                  id: 0,
                  nome: '',
                  host: '',
                  porta: 9100,
                  dpi: 203,
                  padrao: printers.length === 0,
                })
              }
            >
              Nova impressora
            </Button>
          </Box>
          <Table size="small" aria-label="Impressoras">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Endereço (IP ou nome)</TableCell>
                <TableCell align="right">Porta</TableCell>
                <TableCell align="right">DPI</TableCell>
                <TableCell>Padrão</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {printers.map((p) => (
                <TableRow key={p.id}>
                  <TableCell style={{ fontWeight: 600 }}>{p.nome}</TableCell>
                  <TableCell>{p.host}</TableCell>
                  <TableCell align="right">{p.porta}</TableCell>
                  <TableCell align="right">{p.dpi}</TableCell>
                  <TableCell>{p.padrao ? '★ Sim' : '—'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      title="Imprimir etiqueta de teste"
                      onClick={() => testPrinter(p)}
                    >
                      <PrintOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Editar"
                      onClick={() => setPrinterForm(p)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Excluir"
                      onClick={() => deletePrinter(p)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {printers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="textSecondary">
                      Nenhuma impressora ainda. Cadastre a Zebra do CD (mesma
                      rede Wi-Fi do coletor) e use a etiqueta de teste para
                      validar a ponte inteira.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Dialog
            open={printerForm !== null}
            onClose={() => setPrinterForm(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              {printerForm && printerForm.id > 0
                ? 'Editar impressora'
                : 'Nova impressora'}
            </DialogTitle>
            {printerForm && (
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  margin="dense"
                  size="small"
                  variant="outlined"
                  label="Nome (ex.: Zebra CD-01)"
                  value={printerForm.nome}
                  onChange={(e) =>
                    setPrinterForm({ ...printerForm, nome: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  variant="outlined"
                  label="Endereço na rede (IP ou nome)"
                  placeholder="192.168.0.50"
                  value={printerForm.host}
                  onChange={(e) =>
                    setPrinterForm({ ...printerForm, host: e.target.value })
                  }
                />
                <Box display="flex" style={{ gap: 10 }}>
                  <TextField
                    margin="dense"
                    size="small"
                    variant="outlined"
                    type="number"
                    label="Porta"
                    value={printerForm.porta}
                    onChange={(e) =>
                      setPrinterForm({
                        ...printerForm,
                        porta: Number(e.target.value) || 9100,
                      })
                    }
                  />
                  <TextField
                    select
                    margin="dense"
                    size="small"
                    variant="outlined"
                    label="DPI"
                    value={printerForm.dpi}
                    onChange={(e) =>
                      setPrinterForm({
                        ...printerForm,
                        dpi: Number(e.target.value),
                      })
                    }
                    style={{ minWidth: 100 }}
                  >
                    {[203, 300, 600].map((dpi) => (
                      <MenuItem key={dpi} value={dpi}>
                        {dpi}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={printerForm.padrao}
                      onChange={(e) =>
                        setPrinterForm({
                          ...printerForm,
                          padrao: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Impressora padrão"
                />
              </DialogContent>
            )}
            <DialogActions>
              <Button onClick={() => setPrinterForm(null)}>Cancelar</Button>
              <Button variant="contained" color="primary" onClick={savePrinter}>
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}

      {/* ===== FILA ===== */}
      {tab === 2 && (
        <Paper style={{ padding: 16 }}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <IconButton size="small" onClick={loadQueue} title="Atualizar">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          {jobs === null ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small" aria-label="Fila de impressão">
              <TableHead>
                <TableRow>
                  <TableCell>Etiqueta</TableCell>
                  <TableCell>Impressora</TableCell>
                  <TableCell align="right">Cópias</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => {
                  const chip = JOB_CHIP[job.status] || JOB_CHIP.pending;
                  return (
                    <TableRow key={job.id}>
                      <TableCell style={{ fontWeight: 600 }}>
                        {job.descricao}
                      </TableCell>
                      <TableCell>{job.impressora}</TableCell>
                      <TableCell align="right">{job.copias}</TableCell>
                      <TableCell>
                        <span
                          style={{
                            background: chip.bg,
                            color: chip.fg,
                            fontSize: 11,
                            fontWeight: 800,
                            padding: '2px 9px',
                            borderRadius: 999,
                          }}
                        >
                          {chip.label}
                        </span>
                        {job.erro && (
                          <Typography
                            variant="caption"
                            component="div"
                            style={{ color: '#C43D3D' }}
                          >
                            {job.erro}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {job.status === 'pending' && (
                          <IconButton
                            size="small"
                            title="Cancelar"
                            onClick={async () => {
                              await api.delete(`/labels/fila/${job.id}`);
                              loadQueue();
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                        {job.status === 'failed' && (
                          <IconButton
                            size="small"
                            color="primary"
                            title="Tentar de novo"
                            onClick={async () => {
                              await api.post(
                                `/labels/fila/${job.id}/tentar-de-novo`,
                              );
                              loadQueue();
                            }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {jobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="textSecondary">
                        Fila vazia. Imprima pela coluna Ações dos produtos do
                        pedido — o coletor com o app Operation aberto drena a
                        fila sozinho.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Main>
  );
};
